/**
 * Hafif i18n altyapısı — DB (Init) çevirileri öncelikli.
 *
 * Kaynak sırası (t):
 *   1. dbDict[key]  ← /Init/InitApp'ten gelen DB çevirisi (düz kod: _email_address_)
 *   2. key          ← placeholder (DB'de karşılığı yoksa)
 *
 * Statik locale JSON fallback'i kaldırıldı; tüm çeviriler DB'den gelir.
 *
 * Tek InitApp çağrısı: açılış verisi (translate/language/lang) redux'ta
 * getApps tarafından yüklenir; I18nProvider başlangıçta bunu redux'tan OKUR
 * (localStorage cache YOK — büyük çeviri blob'unu senkron JSON parse etmek
 * ana thread'i kasıyordu). Çeviriler sadece dil DEĞİŞİNCE yeniden çekilir.
 *
 * Dil değişimi ATOMİKTİR: yeni sözlük gelene kadar arayüz eski dilde kalır,
 * sonra dil + sözlük TEK adımda uygulanır (bkz. `applied`). Yarım çevrilmiş
 * ekran ya da "F5'e kadar eski dilde kalan" bileşen olmaz.
 *
 * Persian (fa) RTL — language değişince <html dir> set edilir.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import moment from 'moment';
// Tarih/saat formatlarının (moment) aktif dile göre yerelleşmesi için locale verileri.
import 'moment/locale/tr';
import 'moment/locale/de';
import 'moment/locale/ru';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import { setDict } from './dict';
import { API_BASE, DEFAULT_LANG as DEPLOY_LANG } from '../Api/Config'

const RTL_LANGS = new Set(['fa']);
const STORAGE_KEY = 'bonus.lang';
const DEFAULT_LANG = 'tr';

/** Dil koduna göre bayrak emojisi (DB flag alanı çoğunlukla boş/svg dosya adı). */
const FLAG_EMOJI = {
    tr: '🇹🇷', en: '🇬🇧', de: '🇩🇪', ru: '🇷🇺', fa: '🇮🇷', zh: '🇨🇳', hi: '🇮🇳',
    fr: '🇫🇷', es: '🇪🇸', pl: '🇵🇱', it: '🇮🇹', ja: '🇯🇵', ko: '🇰🇷', ar: '🇸🇦',
};

/**
 * InitApp'tan gelen locales satırlarını switcher formatına çevirir.
 * - status === 1 olanlar; `ordered` artan sırada; `lang` koduna göre tekilleştirilir.
 */
const toLangs = (locales) => {
    const seen = new Set();
    return (Array.isArray(locales) ? locales : [])
        // Panel dili: panel=1 VE status=1 (ürün/rehber dilleri burada gösterilmez).
        .filter((l) => l && Number(l.status) === 1 && Number(l.panel) === 1 && l.lang)
        .sort((a, b) => (Number(a.ordered) || 0) - (Number(b.ordered) || 0))
        .reduce((acc, l) => {
            const code = String(l.lang).toLowerCase();
            if (seen.has(code)) return acc;
            seen.add(code);
            acc.push({
                code,
                label: l.name || l.latinName || code.toUpperCase(),
                flag: FLAG_EMOJI[code] || '🏳️',
                localeCode: l.localeCode || null,
            });
            return acc;
        }, []);
};

/**
 * Dağıtımın TANIDIĞI dil kodları — panel dili OLMAYAN site dilleri (nl, pl, it…) dahil.
 *
 * Dil seçimi iki yerde doğrulanıyordu ve ikisi AYNI cevabı vermiyordu: sayfa açılışı
 * localStorage'daki kodu sorgusuz kabul ederken, canlı geçiş sabit bir listeye bakıp
 * reddediyordu. Sonuç: NL seçilince arayüz Türkçe kalıyor, F5'ten sonra Hollandaca
 * açılıyordu. Kabul ölçütü artık tek: `locales` ne diyorsa o.
 */
const toKnownCodes = (locales) => new Set(
    (Array.isArray(locales) ? locales : [])
        .filter((l) => l && Number(l.status) === 1 && (Number(l.panel) === 1 || Number(l.sites) === 1) && l.lang)
        .map((l) => String(l.lang).toLowerCase())
);

/** Init endpoint'i: çeviri map'i + dil listesi (login öncesi de erişilebilir, token gerekmez). */
const fetchInit = async (lang) => {
    try {
        const url = (API_BASE || '') + '/Init/InitApp';
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ browserLanguage: lang }),
        });
        if (!res.ok) return null;
        const json = await res.json();
        const p = json?.payload;
        if (!p || typeof p !== 'object') return null;
        return {
            translate: p.translate && typeof p.translate === 'object' ? p.translate : null,
            language: Array.isArray(p.language) ? p.language : null,
        };
    } catch {
        return null;
    }
};

const I18nContext = createContext({
    lang: DEFAULT_LANG,
    setLang: () => {},
    t: (k) => k,
    langs: [],
});

const SUPPORTED_LANGS = ['tr', 'en', 'de', 'ru', 'es', 'fr', 'it'];
const resolveInitialLang = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) return saved; // kullanıcının açık seçimi öncelikli
    } catch { /* SSR/private mode */ }
    // Dağıtım bir dil dayatıyorsa (runtime-config.js → Holideus RU 'ru') tarayıcı dilinden
    // ÖNCE gelir: o pazar için kurulmuş bir dağıtım, ziyaretçinin tarayıcısı ne derse desin
    // kendi dilinde açılmalı. TourZone'da bu değer BOŞ → aşağıdaki eski davranış işler.
    if (DEPLOY_LANG && SUPPORTED_LANGS.includes(DEPLOY_LANG)) return DEPLOY_LANG;
    // Açık seçim yoksa tarayıcı dilini tespit et (en-US -> en, tr-TR -> tr).
    try {
        const nav = (navigator.language || (navigator.languages && navigator.languages[0]) || '').slice(0, 2).toLowerCase();
        if (SUPPORTED_LANGS.includes(nav)) return nav;
    } catch { /* yutulur */ }
    return DEFAULT_LANG;
};

const interpolate = (str, params) => {
    if (!params) return str;
    return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (params[k] ?? `{{${k}}}`));
};

export const I18nProvider = ({ children }) => {
    const store = useStore();
    const initialLang = store.getState().Apps?.lang || resolveInitialLang();
    // İSTENEN dil (kullanıcının seçtiği) — sözlük çekmeyi tetikler, localStorage'a yazılır.
    const [lang, setLangState] = useState(initialLang);
    // UYGULANAN dil + sözlüğü BİRLİKTE tutulur. AYRI tutulsalardı dil değişiminde
    // sözlük bir tur eski kalırdı; aşağıdaki remount o eski sözlükle çalışır ve
    // getTranslate kullanan (reaktif OLMAYAN, ~161 dosya) bileşenler ESKİ DİLDE
    // ÇAKILI kalırdı — sayfa yenilenene kadar. "Bazı yerler çevrilmiyor, F5'ten
    // sonra düzeliyor" şikayetinin sebebi buydu.
    const [applied, setApplied] = useState(() => ({
        lang: initialLang,
        dict: store.getState().Apps?.translate || {},
    }));
    const [langs, setLangs] = useState(() => toLangs(store.getState().Apps?.language));
    // Seçilebilir TÜM diller (switcher listesinden geniş: ürün sayfası site dillerini de sunar).
    const [known, setKnown] = useState(() => toKnownCodes(store.getState().Apps?.language));

    // Hook'suz yerler (getTranslate) için modül sözlüğünü SENKRON güncelle (render sırasında).
    // useEffect ile yapılırsa modül sözlüğü bir render geç kalır.
    setDict(applied.lang, applied.dict);
    // moment yereli de UYGULANAN dile bağlı → tarih formatı metinle aynı anda değişir.
    if (moment.locale() !== applied.lang) {
        moment.locale(applied.lang);
    }

    useEffect(() => {
        const isRtl = RTL_LANGS.has(applied.lang);
        document.documentElement.setAttribute('lang', applied.lang);
        document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    }, [applied.lang]);

    useEffect(() => {
        // Sözlük istenen dile AİTSE (ve boş değilse) çekecek bir şey yok — açılışta
        // redux zaten verdiği için ilk çağrı da böylece atlanır.
        if (lang === applied.lang && Object.keys(applied.dict).length > 0) return undefined;

        let mounted = true;
        fetchInit(lang).then((fresh) => {
            if (!mounted || !fresh) return;
            if (fresh.language) { setLangs(toLangs(fresh.language)); setKnown(toKnownCodes(fresh.language)); }
            // Dil ve sözlük TEK setState ile birlikte uygulanır → remount yeni sözlükle olur.
            // Çekme başarısızsa hiçbir şey değişmez: yarım çevrilmiş ekran yerine eski dil.
            if (fresh.translate) setApplied({ lang, dict: fresh.translate });
        });
        return () => { mounted = false; };
    }, [lang, applied.lang, applied.dict]);

    const setLang = useCallback((next) => {
        if (!next) return;
        // Ölçüt TEK: dağıtımın tanıdığı dil mi (locales). Liste henüz gelmediyse
        // (açılışın ilk anı) sabit listeye düşülür. Sayfa açılışındaki kabul ölçütü
        // de budur — ikisi ayrışırsa seçim "yenileyince tutuyor, canlıda tutmuyor"
        // gibi davranır.
        const ok = known.size > 0 ? known.has(next) : SUPPORTED_LANGS.includes(next);
        if (!ok) return;
        // Kalıcılık BURADA yapılır: başka bir yer localStorage'a yazarsa doğrulamayı
        // atlamış olur (ProductView tam olarak bunu yapıyordu).
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* yutulur */ }
        setLangState(next);
    }, [known]);

    const t = useCallback((key, params) => {
        if (!key) return '';
        // DB (Init) çevirisi — düz kod (_email_address_). Karşılığı yoksa kod placeholder döner.
        const dbVal = applied.dict[key];
        if (typeof dbVal === 'string') return interpolate(dbVal, params);
        return key;
    }, [applied.dict]);

    // `lang` = UYGULANAN dil (ekranda gerçekten görünen). `pendingLang` = seçilip
    // sözlüğü henüz gelmemiş dil; yalnız dil seçicinin anında tepki vermesi için.
    const value = useMemo(
        () => ({ lang: applied.lang, pendingLang: lang, setLang, t, langs }),
        [applied.lang, lang, setLang, t, langs]
    );
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useT = () => useContext(I18nContext);

/**
 * Dil değişince alt ağacı REMOUNT eder (key={lang}).
 * getTranslate (reaktif OLMAYAN, ~161 dosya: class bileşenler/modül fonksiyonları)
 * kullanan yerler ancak remount ile yeni dile geçer; useT/withT zaten reaktif.
 * BrowserRouter DIŞARIDA kaldığı için router/history/URL + redux KORUNUR; yalnız
 * routed içerik (Layout + sayfa) yeni dille yeniden kurulur → tüm metinler güncellenir.
 *
 * DİKKAT: buradaki `lang` UYGULANAN dildir (sözlüğü gelmiş olan), istenen dil değil.
 * İstenen dille anahtarlansaydı remount ESKİ sözlükle olur, sözlük saniyenin
 * kesrinde arkadan gelir ama yeni bir remount tetiklemezdi → getTranslate kullanan
 * bileşenler eski dilde kalırdı (F5 atınca düzelen o hata).
 */
export const I18nRemount = ({ children }) => {
    const { lang } = useContext(I18nContext);
    return <React.Fragment key={lang || DEFAULT_LANG}>{children}</React.Fragment>;
};

/**
 * Class bileşenler için HOC. props.t, props.lang, props.setLang, props.langs sağlar.
 */
export const withT = (Wrapped) => {
    const WithT = (props) => {
        const ctx = useContext(I18nContext);
        return <Wrapped {...props} t={ctx.t} lang={ctx.lang} setLang={ctx.setLang} langs={ctx.langs} />;
    };
    WithT.displayName = `withT(${Wrapped.displayName || Wrapped.name || 'Component'})`;
    return WithT;
};
